import { type FormEvent, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { postLoginPath } from '@/shared/lib/pendingIntentStorage'
import { offerPasskeyEnrollmentAfterLogin } from '@/features/passkeys/lib/passkeyEnrollmentPrompt'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { ApiError } from '@/shared/api/authApi'
import { branding } from '@/shared/config/branding'
import {
  LOGIN_EMAIL_STORAGE_KEY,
  readLastLoginEmail,
  rememberLastLoginEmail,
  validateLoginForm,
} from '@/shared/lib/loginFormUtils'
import { toFriendlyErrorMessage } from '@/shared/lib/errorMessage'
import { ErrorMessageWithCopy } from '@/shared/ui/ErrorMessageWithCopy'
import { useInstallAppPrompt } from '@/shared/lib/useInstallAppPrompt'
import { Button } from '@/shared/ui/Button'
import { TextInput } from '@/shared/ui/TextInput'
import { PasswordInput } from '@/shared/ui/PasswordInput'
import { useConfirmDialog } from '@/shared/ui/useConfirmDialog'
import { useToast } from '@/shared/ui/toast/useToast'
import { AuthHeader } from '@/widgets/login/AuthHeader'
import { PasskeyLoginButton } from '@/widgets/login/PasskeyLoginButton'
import { GoogleLoginButton } from '@/widgets/login/GoogleLoginButton'
import { checkForPwaUpdateAndToast } from '@/shared/pwa/checkForPwaUpdateAndToast'

const readInitialLoginEmail = (): string => {
  if (typeof window === 'undefined') return ''
  return (
    sessionStorage.getItem(LOGIN_EMAIL_STORAGE_KEY)?.trim() ||
    readLastLoginEmail() ||
    ''
  )
}

export const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { confirm } = useConfirmDialog()
  const { success, info } = useToast()
  const loginWithCredentials = useSessionStore((s) => s.loginWithCredentials)
  const user = useSessionStore((s) => s.user)
  const loading = useSessionStore((s) => s.loading)
  const loginStatus = useSessionStore((s) => s.loginStatus)
  const loginError = useSessionStore((s) => s.loginError)
  const { canInstall, isInstalling, installApp, isInBrowserMode } = useInstallAppPrompt()

  const [email, setEmail] = useState(readInitialLoginEmail)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (loginError) queueMicrotask(() => setError(loginError))
  }, [loginError])

  useEffect(() => {
    const oauthError = (location.state as { oauthError?: string } | null)?.oauthError
    if (oauthError) {
      queueMicrotask(() => setError(oauthError))
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  useEffect(() => {
    const t = window.setTimeout(() => {
      void checkForPwaUpdateAndToast()
    }, 2500)
    return () => window.clearTimeout(t)
  }, [])

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    const validationError = validateLoginForm(email, password)
    if (validationError) {
      setError(validationError)
      return
    }

    const trimmedEmail = email.trim()
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(LOGIN_EMAIL_STORAGE_KEY, trimmedEmail)
      }
      await loginWithCredentials(trimmedEmail, password)
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(LOGIN_EMAIL_STORAGE_KEY)
        rememberLastLoginEmail(trimmedEmail)
      }

      const loggedInUserId = useSessionStore.getState().user?.id ?? user?.id
      if (loggedInUserId) {
        await offerPasskeyEnrollmentAfterLogin({
          userId: loggedInUserId,
          confirm,
          onRegistered: () => {
            success('Chave de acesso cadastrada. Na próxima vez, use biometria ou PIN para entrar.')
          },
          onDeclined: () => {
            info(
              'Sem problema. Você pode cadastrar uma chave de acesso depois em Ajustes → Chaves de acesso.',
              { durationMs: 5000 },
            )
          },
        })
      }

      navigate(postLoginPath(), { replace: true })
    } catch (err) {
      const msg = toFriendlyErrorMessage(
        err,
        err instanceof ApiError ? err.message : 'Não foi possível concluir o login.',
      )
      setError(msg)
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-[430px] flex-col justify-start bg-[var(--color-page)] px-6 pb-10 pt-6">
      <main className="space-y-5">
        <h1 className="sr-only">Entrar</h1>
        <AuthHeader subtitle={branding.slogan} showHeartbeat />

        <form onSubmit={handleLogin} className="space-y-3">
          <TextInput
            label="Email"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username email"
          />
          <PasswordInput
            label="Senha"
            placeholder="Mínimo 8 caracteres"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            minLength={8}
          />

          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="rounded-xl border border-[var(--color-wine-soft-border)] bg-[var(--color-wine-soft)] px-3 py-2"
            >
              <ErrorMessageWithCopy message={error} textClassName="text-xs font-semibold" />
            </div>
          )}

          <Button type="submit" fullWidth disabled={loading} aria-busy={loading || undefined}>
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>

          {loading && loginStatus ? (
            <p className="text-center text-xs text-[var(--color-text-secondary)]" role="status" aria-live="polite">
              {loginStatus}
            </p>
          ) : null}

          {canInstall ? (
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => void installApp()}
              disabled={isInstalling}
            >
              {isInstalling ? 'Abrindo instalador...' : 'Instalar App'}
            </Button>
          ) : null}

          {isInBrowserMode && !canInstall ? (
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => {
                window.location.replace('/')
              }}
            >
              Abrir App
            </Button>
          ) : null}
        </form>

        <div className="space-y-3">
          <div className="flex items-center gap-3 py-1" aria-hidden="true">
            <span className="h-px flex-1 bg-[var(--color-border)]" />
            <span className="text-xs font-medium text-[var(--color-text-muted)]">ou</span>
            <span className="h-px flex-1 bg-[var(--color-border)]" />
          </div>

          <GoogleLoginButton />

          <PasskeyLoginButton />
        </div>

        <div className="flex items-center justify-between gap-3 text-xs">
          <Link
            to="/register"
            className="font-semibold text-[var(--color-link)] underline-offset-2 hover:underline"
          >
            Criar conta
          </Link>
          <Link
            to="/forgot-password"
            className="font-semibold text-[var(--color-link)] underline-offset-2 hover:underline"
          >
            Esqueceu a senha?
          </Link>
        </div>

        <p className="mt-6 text-center">
          <Link
            to="/policy"
            className="text-xs text-[var(--color-text-secondary)] underline-offset-2 hover:underline"
          >
            Termos de Uso e Política de Privacidade
          </Link>
        </p>
      </main>
    </div>
  )
}
